import { formatDate as formatDateShared } from '../../../../utils/formatUtils';
import {
  validateCUIT,
  validateEmail as validateEmailShared,
  validatePhone as validatePhoneShared,
} from '../../../../utils/validationUtils';

/**
 * Filtra subcontratistas según término de búsqueda
 * @param {Array} subcontractors - Lista de subcontratistas
 * @param {string} searchTerm - Término de búsqueda
 * @returns {Array} - Lista filtrada
 */
export const filterSubcontractors = (subcontractors = [], searchTerm = '') => {
  const normalizedTerm = searchTerm?.toLowerCase().trim();

  return subcontractors.filter((subcontractor) => {
    const company = subcontractor.company?.toLowerCase() ?? '';
    const legalBusinessName = subcontractor.legal_business_name?.toLowerCase() ?? '';
    const cuit = subcontractor.cuit?.toLowerCase() ?? '';
    const contactInformation = subcontractor.contact_information?.toLowerCase() ?? '';
    const address = subcontractor.address?.toLowerCase() ?? '';

    const matchesTerm =
      !normalizedTerm ||
      company.includes(normalizedTerm) ||
      legalBusinessName.includes(normalizedTerm) ||
      cuit.includes(normalizedTerm) ||
      contactInformation.includes(normalizedTerm) ||
      address.includes(normalizedTerm);

    return matchesTerm;
  });
};

/**
 * Valida un CUIT argentino
 * @param {string} cuit - CUIT a validar
 * @returns {boolean} - True si es válido
 */
export const validateCuit = (cuit) => {
  if (!cuit) return true; // CUIT es opcional según el form
  return validateCUIT(cuit);
};

/**
 * Valida un email
 * @param {string} email - Email a validar
 * @returns {boolean} - True si es válido
 */
export const validateEmail = (email) => validateEmailShared(email, { optional: true });

/**
 * Valida un teléfono
 * @param {string} phone - Teléfono a validar
 * @returns {boolean} - True si es válido
 */
export const validatePhone = (phone) => validatePhoneShared(phone, { optional: true });

/**
 * Prepara los datos del subcontratista para envío a la API
 * @param {Object} subcontractor - Datos del subcontratista
 * @returns {Object} - Datos preparados para la API
 */
export const prepareSubcontractorData = (subcontractor) => {
  const cleanedData = { ...subcontractor };

  // Limpiar campos vacíos (excepto los booleanos)
  Object.keys(cleanedData).forEach((key) => {
    if (
      typeof cleanedData[key] !== 'boolean' &&
      (cleanedData[key] === '' || cleanedData[key] === null)
    ) {
      delete cleanedData[key];
    }
  });

  // Asegurar flags correctos
  cleanedData.is_subcontractor = true;
  cleanedData.is_provider = true;
  cleanedData.entity_type = 'provider';

  return cleanedData;
};

/**
 * Formatea una fecha para mostrar
 * @param {string} date - Fecha en formato ISO
 * @returns {string} - Fecha formateada
 */
export const formatDate = (date) => formatDateShared(date, { fallback: '' });

/**
 * Obtiene el nombre de un distrito por UUID
 * @param {Array} districts - Lista de distritos
 * @param {string} uuid - UUID del distrito
 * @returns {string} - Nombre del distrito
 */
export const getDistrictName = (districts, uuid) => {
  const district = districts?.find((d) => d.uuid === uuid);
  return district?.name || 'Sin distrito';
};

/**
 * Obtiene la ubicación completa de un subcontratista
 * @param {Object} subcontractor - Subcontratista
 * @param {Array} countries - Lista de países
 * @param {Array} provinces - Lista de provincias
 * @param {Array} departments - Lista de departamentos
 * @param {Array} districts - Lista de distritos
 * @returns {Object} - Ubicación completa
 */
export const getSubcontractorLocation = (
  subcontractor,
  countries,
  provinces,
  departments,
  districts
) => {
  const district = districts?.find((d) => d.uuid === subcontractor.uuid_district);
  const department = departments?.find((d) => d.uuid === district?.uuid_department);
  const province = provinces?.find((p) => p.uuid === department?.uuid_province);
  const country = countries?.find((c) => c.uuid === province?.uuid_country);

  return {
    district,
    department,
    province,
    country,
  };
};


/**
 * Formatea el período de contrato para mostrar
 * @param {string} startDate - Fecha de inicio
 * @param {string} endDate - Fecha de fin
 * @returns {string} - Período formateado
 */
export const formatContractPeriod = (startDate, endDate) => {
  if (!startDate && !endDate) return 'Sin definir';
  if (!startDate) return `Hasta ${formatDate(endDate)}`;
  if (!endDate) return `Desde ${formatDate(startDate)}`;
  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

/**
 * Obtiene el texto de la calificación
 * @param {string} rating - Valor de calificación (1-5)
 * @returns {string} - Texto de la calificación
 */
export const getRatingText = (rating) => {
  const ratingMap = {
    1: '★☆☆☆☆',
    2: '★★☆☆☆',
    3: '★★★☆☆',
    4: '★★★★☆',
    5: '★★★★★',
  };
  return ratingMap[rating] || 'Sin calificar';
};

/**
 * Obtiene el texto del tipo de servicio
 * @param {string} serviceType - Tipo de servicio
 * @returns {string} - Texto del tipo de servicio
 */
export const getServiceTypeText = (serviceType) => {
  const serviceMap = {
    construccion: 'Construcción',
    limpieza: 'Limpieza',
    seguridad: 'Seguridad',
    mantenimiento: 'Mantenimiento',
    consultoria: 'Consultoría',
    otros: 'Otros',
  };
  return serviceMap[serviceType] || serviceType;
};

/**
 * Valida si un contrato está vigente
 * @param {string} startDate - Fecha de inicio
 * @param {string} endDate - Fecha de fin
 * @returns {boolean} - True si está vigente
 */
export const isContractActive = (startDate, endDate) => {
  const now = new Date();
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;

  if (start && start > now) return false;
  if (end && end < now) return false;
  return true;
};
